// src/components/window.tsx
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  CSSProperties,
} from "react";
import { theme } from "@/styles/theme";

type Offset = { x: number; y: number };

type WindowSizeConfig = {
  /** Target width for desktop (px or CSS string). Mobile will ignore this. */
  desktopWidth?: number | string;
  /** Fixed / max height for desktop window content. */
  desktopMaxHeight?: number | string;

  /** Target width for mobile viewport (defaults to 92vw). */
  mobileWidth?: number | string;
  /** Fixed frame height on mobile (if omitted, uses mobileMaxHeight). */
  mobileHeight?: number | string;
  /** Max height cap on mobile (defaults to 70vh). */
  mobileMaxHeight?: number | string;
};

export type Win95WindowProps = {
  title: string;
  children: React.ReactNode;
  zIndex: number;
  onFocus?: () => void;

  /** Called when the close animation has fully finished */
  onClosed: () => void;

  /** Called when user clicks the X – parent should request close (nav button can also use same path) */
  onRequestClose: () => void;

  /** Increment this to request a close with trail (from nav button / X) */
  closeRequestKey: number;

  /** Icon to show in the title bar (e.g. /images/Icon-Projects.png) */
  iconSrc?: string;
  iconAlt?: string;

  /** Where the window should "come from" on open (nav button center) */
  anchorRect?: DOMRect | null;

  /** Final offset from screen center (random per open on desktop) */
  targetOffset?: Offset | null;

  /** Optional extra styles for the body content (layout per-window) */
  bodyStyle?: CSSProperties;

  /** Per-window desktop & mobile sizing */
  windowSize?: WindowSizeConfig;
};

type Phase = "opening" | "idle" | "closing";

// ──────────────────────────────────────────────
// Simple local mobile detector
// ──────────────────────────────────────────────
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const update = () => setIsMobile(window.innerWidth <= breakpoint);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [breakpoint]);

  return isMobile;
}

export function Win95Window({
  title,
  children,
  zIndex,
  onFocus,
  onClosed,
  onRequestClose,
  closeRequestKey,
  iconSrc,
  iconAlt,
  anchorRect,
  targetOffset,
  bodyStyle,
  windowSize,
}: Win95WindowProps) {
  const [phase, setPhase] = useState<Phase>("opening");
  const [animT, setAnimT] = useState(0); // 0..1 along the path
  const [fromDelta, setFromDelta] = useState<Offset>({ x: 0, y: 0 });
  const [offset, setOffset] = useState<Offset>({ x: 0, y: 0 }); // drag offset

  const animFrameRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number | null>(null);
  const prevCloseKeyRef = useRef(closeRequestKey);

  // Dragging state
  const dragState = useRef({
    dragging: false,
    startX: 0,
    startY: 0,
    startOffsetX: 0,
    startOffsetY: 0,
  });

  // Close button visual state
  const [closeHovered, setCloseHovered] = useState(false);
  const [closePressed, setClosePressed] = useState(false);

  const isMobile = useIsMobile();

  // ──────────────────────────────────────────────
  // Sizing (desktop + mobile)
  // ──────────────────────────────────────────────
  const DEFAULT_MOBILE_WIDTH = "92vw";
  const DEFAULT_MOBILE_MAX_HEIGHT = "70vh";

  const desktopWidth = windowSize?.desktopWidth ?? 520;
  const desktopMaxHeight = windowSize?.desktopMaxHeight ?? "80vh";

  const mobileWidth = windowSize?.mobileWidth ?? DEFAULT_MOBILE_WIDTH;
  const mobileMaxHeight =
    windowSize?.mobileMaxHeight ?? DEFAULT_MOBILE_MAX_HEIGHT;
  const mobileHeight = windowSize?.mobileHeight ?? mobileMaxHeight;

  const resolvedWidth = isMobile ? mobileWidth : desktopWidth;
  const resolvedMaxWidth = isMobile ? mobileWidth : "90vw";
  const resolvedMaxHeight = isMobile ? mobileMaxHeight : desktopMaxHeight;
  const resolvedFrameHeight = isMobile ? mobileHeight : desktopMaxHeight;

  // ──────────────────────────────────────────────
  // Helpers
  // ──────────────────────────────────────────────

  const stopAnimation = () => {
    if (animFrameRef.current !== null) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
  };

  const computeOpenFromDelta = () => {
    if (!anchorRect || typeof window === "undefined") {
      setFromDelta({ x: 0, y: 0 });
      return;
    }
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const centerX = vw / 2;
    const centerY = vh / 2;

    const baseOffset = targetOffset ?? { x: 0, y: 0 };
    const finalX = centerX + baseOffset.x;
    const finalY = centerY + baseOffset.y;

    const fromX = anchorRect.left + anchorRect.width / 2;
    const fromY = anchorRect.top + anchorRect.height / 2;

    setFromDelta({ x: fromX - finalX, y: fromY - finalY });
  };

  const computeCloseFromDelta = () => {
    if (!anchorRect || typeof window === "undefined") {
      setFromDelta({ x: 0, y: 0 });
      return;
    }
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const centerX = vw / 2;
    const centerY = vh / 2;

    const baseOffset = targetOffset ?? { x: 0, y: 0 };
    // CURRENT window center (includes drag offset)
    const finalX = centerX + baseOffset.x + offset.x;
    const finalY = centerY + baseOffset.y + offset.y;

    const fromX = anchorRect.left + anchorRect.width / 2;
    const fromY = anchorRect.top + anchorRect.height / 2;

    setFromDelta({ x: fromX - finalX, y: fromY - finalY });
  };

  const runAnimation = (mode: "opening" | "closing") => {
    stopAnimation();
    lastTimeRef.current = null;
    const duration = 0.15; // seconds

    const step = (timestamp: number) => {
      if (lastTimeRef.current == null) {
        lastTimeRef.current = timestamp;
        animFrameRef.current = requestAnimationFrame(step);
        return;
      }

      const dt = (timestamp - lastTimeRef.current) / 1000;
      lastTimeRef.current = timestamp;

      setAnimT((prev) => {
        let next = prev;
        if (mode === "opening") {
          next = Math.min(1, prev + dt / duration);
          if (next >= 1) {
            setPhase("idle");
            stopAnimation();
            return 1;
          }
        } else {
          next = Math.max(0, prev - dt / duration);
          if (next <= 0) {
            setPhase("idle");
            stopAnimation();
            // Let parent know close is done so it can unmount
            onClosed();
            return 0;
          }
        }
        animFrameRef.current = requestAnimationFrame(step);
        return next;
      });
    };

    animFrameRef.current = requestAnimationFrame(step);
  };

  // t in [0..1] → transform from nav button to window (for open),
  // or from window to nav (for close) depending on phase & fromDelta.
  const computeTransform = (t: number) => {
    const baseOffset = targetOffset ?? { x: 0, y: 0 };

    const finalXOffset = baseOffset.x + offset.x;
    const finalYOffset = baseOffset.y + offset.y;

    const posX = finalXOffset + fromDelta.x * (1 - t);
    const posY = finalYOffset + fromDelta.y * (1 - t);

    const scaleStart = 0.3; // small square at t=0
    const scale = scaleStart + (1 - scaleStart) * t;

    return `translate(-50%, -50%) translate(${posX}px, ${posY}px) scale(${scale})`;
  };

  // ──────────────────────────────────────────────
  // Drag handlers
  // ──────────────────────────────────────────────

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!dragState.current.dragging) return;
    const dx = e.clientX - dragState.current.startX;
    const dy = e.clientY - dragState.current.startY;
    setOffset({
      x: dragState.current.startOffsetX + dx,
      y: dragState.current.startOffsetY + dy,
    });
  }, []);

  const handleMouseUp = useCallback(() => {
    if (dragState.current.dragging) {
      dragState.current.dragging = false;
    }
    window.removeEventListener("mousemove", handleMouseMove);
    window.removeEventListener("mouseup", handleMouseUp);
  }, [handleMouseMove]);

  const handleTitleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      dragState.current.dragging = true;
      dragState.current.startX = e.clientX;
      dragState.current.startY = e.clientY;
      dragState.current.startOffsetX = offset.x;
      dragState.current.startOffsetY = offset.y;

      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);

      onFocus?.();
    },
    [offset.x, offset.y, handleMouseMove, handleMouseUp, onFocus]
  );

  // ──────────────────────────────────────────────
  // Lifecycle
  // ──────────────────────────────────────────────

  // On mount: compute open path and play opening animation
  useEffect(() => {
    if (!anchorRect || typeof window === "undefined") {
      // No anchor: appear directly, no trail
      setPhase("idle");
      setAnimT(1);
      return;
    }

    computeOpenFromDelta();
    setPhase("opening");
    setAnimT(0);
    runAnimation("opening");

    return () => {
      stopAnimation();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Respond to external close requests (nav button or X via parent)
  useEffect(() => {
    if (closeRequestKey === prevCloseKeyRef.current) return;
    prevCloseKeyRef.current = closeRequestKey;

    if (!anchorRect || typeof window === "undefined") {
      // No path, just close
      onClosed();
      return;
    }

    // Recompute path from current position → nav button
    computeCloseFromDelta();
    setPhase("closing");
    setAnimT(1);
    runAnimation("closing");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closeRequestKey]);

  // Clean up listeners and animation on unmount
  useEffect(() => {
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      stopAnimation();
    };
  }, [handleMouseMove, handleMouseUp]);

  // ──────────────────────────────────────────────
  // Close button visuals
  // ──────────────────────────────────────────────

  let closeBg = theme.colors.Black;
  let closeBorder = theme.colors.White;
  let closeColor = theme.colors.White;

  if (closePressed) {
    closeBg = theme.colors.DarkGray;
    closeBorder = theme.colors.LightGreen;
    closeColor = theme.colors.LightGreen;
  } else if (closeHovered) {
    closeBg = theme.colors.DarkGray;
    closeBorder = theme.colors.White;
    closeColor = theme.colors.White;
  }

  // ──────────────────────────────────────────────
  // Wireframe trail rectangles
  // ──────────────────────────────────────────────

  const isAnimating = phase === "opening" || phase === "closing";
  const trailSteps = 3;
  const trailLag = 0.06;

  const wireframes: React.ReactElement[] = [];
  if (isAnimating) {
    for (let i = 0; i < trailSteps; i++) {
      const lagIndex = trailSteps - 1 - i;
      let localT = animT - lagIndex * trailLag;
      if (localT <= 0) continue;
      if (localT > 1) localT = 1;

      const opacity =
        0.25 + (i / (trailSteps - 1)) * 0.4; // back dimmer, front brighter

      wireframes.push(
        <div
          key={i}
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: computeTransform(localT),
            transformOrigin: "center center",
            minWidth: 320,
            minHeight: 140,
            border: `1px solid ${theme.colors.Green}`,
            background: "transparent",
            pointerEvents: "none",
            zIndex: zIndex - 1,
            opacity,
          }}
        />
      );
    }
  }

  const showSolidWindow = phase === "idle" && animT >= 1;

  return (
    <>
      {wireframes}

      {showSolidWindow && (
        <div
          onMouseDown={() => onFocus?.()}
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: computeTransform(1),
            transformOrigin: "center center",
            background: theme.colors.Black,
            color: theme.colors.Text,
            border: `1px solid ${theme.colors.White}`,

            // Responsive shared sizing logic
            width: resolvedWidth,
            maxWidth: resolvedMaxWidth,
            maxHeight: resolvedMaxHeight,
            height: resolvedFrameHeight,
            minWidth: isMobile ? 260 : 320,

            display: "flex",
            flexDirection: "column",
            overflow: "hidden", // frame never scrolls

            fontFamily: theme.fonts.body,
            zIndex,
            pointerEvents: "auto",
          }}
        >
          {/* Title bar */}
          <div
            onMouseDown={handleTitleMouseDown}
            style={{
              margin: "1px 1px 0 1px",
              border: `1px solid ${theme.colors.Black}`,
              background: theme.colors.Green,
              color: theme.colors.Black,
              height: 30,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "2px 4px",
              cursor: "move",
              userSelect: "none",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                minWidth: 0,
                marginLeft: 6,
              }}
            >
              {iconSrc && (
                <img
                  src={iconSrc}
                  alt={iconAlt ?? ""}
                  style={{
                    width: 24,
                    height: 24,
                    imageRendering: "pixelated",
                  }}
                />
              )}
              <span
                style={{
                  fontSize: 17,
                  fontWeight: "bold",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                }}
              >
                {title}
              </span>
            </div>

            {/* Close button – requests close, parent increments closeRequestKey */}
            <button
            type="button"
            onClick={onRequestClose}
            onMouseEnter={() => setCloseHovered(true)}
            onMouseLeave={() => {
              setCloseHovered(false);
              setClosePressed(false);
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
              setClosePressed(true);
            }}
            onMouseUp={(e) => {
              e.stopPropagation();
              setClosePressed(false);
            }}
            style={{
              width: 24,   // keep button hit area comfortable
              height: 22,
              border: `1px solid ${closeBorder}`,
              background: closeBg,
              color: closeColor,
              fontSize: 11,
              fontFamily: theme.fonts.body,
              padding: 0,
              cursor: "pointer",
              lineHeight: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="14"
              viewBox="0 0 16 14"
              fill="none"
              style={{ flexShrink: 0 }}
            >
              <path
                d="M13.5384 3.81814H12.3076V5.09086H11.0769V6.36359H9.84611V7.63632H11.0769V8.90905H12.3076V10.1818H13.5384V11.4545H11.0769V10.1818H9.84611V8.90905H7.38457V10.1818H6.1538V11.4545H3.69226V10.1818H4.92303V8.90905H6.1538V7.63632H7.38457V6.36359H6.1538V5.09086H4.92303V3.81814H3.69226V2.54541H6.1538V3.81814H7.38457V5.09086H9.84611V3.81814H11.0769V2.54541H13.5384V3.81814Z"
                fill="currentColor" // follow closeColor
              />
            </svg>
          </button>

          </div>

          {/* Body: frame area, never scrolls – children manage scroll internally */}
          <div
            style={{
              margin: "0 1px 1px 1px",
              padding: 10,
              fontSize: 13,
              lineHeight: 1.4,
              background: theme.colors.Black,
              color: theme.colors.Text,
              boxSizing: "border-box",

              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden", // critical: no scroll here
              minHeight: 0, // allow children with overflow to scroll

              ...bodyStyle,
            }}
          >
            {children}
          </div>
        </div>
      )}
    </>
  );
}
